function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Log from '@deephaven/log';
import { DateTimeColumnFormatter, DecimalColumnFormatter, IntegerColumnFormatter } from '@deephaven/jsapi-utils';
import merge from 'lodash.merge';
import UserLayoutUtils from "./UserLayoutUtils.js";
var log = Log.module('LocalWorkspaceStorage');

/**
 * Implementation of WorkspaceStorage that just stores the workspace data in localStorage
 */
export class LocalWorkspaceStorage {
  static getBooleanServerConfig(serverConfigValues, key) {
    var _serverConfigValues$g, _serverConfigValues$g2;
    if ((serverConfigValues === null || serverConfigValues === void 0 || (_serverConfigValues$g = serverConfigValues.get(key)) === null || _serverConfigValues$g === void 0 ? void 0 : _serverConfigValues$g.toLowerCase()) === 'true') {
      return true;
    }
    if ((serverConfigValues === null || serverConfigValues === void 0 || (_serverConfigValues$g2 = serverConfigValues.get(key)) === null || _serverConfigValues$g2 === void 0 ? void 0 : _serverConfigValues$g2.toLowerCase()) === 'false') {
      return false;
    }
    return undefined;
  }
  static getJSONServerConfig(serverConfigValues, key) {
    var value = serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get(key);
    if (value == null) {
      return undefined;
    }
    try {
      return JSON.parse(value);
    } catch (e) {
      log.error("Unable to parse JSON server config for key ".concat(key, ". Value: ").concat(value));
      return undefined;
    }
  }
  static makeDefaultWorkspaceSettings(serverConfigValues) {
    var settings = {
      defaultDateTimeFormat: DateTimeColumnFormatter.DEFAULT_DATETIME_FORMAT_STRING,
      formatter: [],
      timeZone: DateTimeColumnFormatter.DEFAULT_TIME_ZONE_ID,
      showTimeZone: false,
      showTSeparator: true,
      disableMoveConfirmation: false,
      defaultDecimalFormatOptions: {
        defaultFormatString: DecimalColumnFormatter.DEFAULT_FORMAT_STRING
      },
      defaultIntegerFormatOptions: {
        defaultFormatString: IntegerColumnFormatter.DEFAULT_FORMAT_STRING
      },
      truncateNumbersWithPound: false,
      showEmptyStrings: true,
      showNullStrings: true,
      showExtraGroupColumn: true,
      notebookSettings: {
        isMinimapEnabled: false,
        formatOnSave: false,
        python: {
          linter: {
            isEnabled: true
            // Omit default config so default settings are used if the user never changes them
          }
        }
      },
      webgl: true,
      webglEditable: true,
      gridDensity: 'regular'
    };
    var serverSettings = {
      defaultDateTimeFormat: serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get('dateTimeFormat'),
      formatter: [],
      timeZone: serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get('timeZone'),
      showTimeZone: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'showTimeZone'),
      showTSeparator: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'showTSeparator'),
      disableMoveConfirmation: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'disableMoveConfirmation'),
      defaultDecimalFormatOptions: (serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get('decimalFormat')) !== undefined ? {
        defaultFormatString: serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get('decimalFormat')
      } : undefined,
      defaultIntegerFormatOptions: (serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get('integerFormat')) !== undefined ? {
        defaultFormatString: serverConfigValues === null || serverConfigValues === void 0 ? void 0 : serverConfigValues.get('integerFormat')
      } : undefined,
      truncateNumbersWithPound: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'truncateNumbersWithPound'),
      showEmptyStrings: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'showEmptyStrings'),
      showNullStrings: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'showNullStrings'),
      showExtraGroupColumn: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'showExtraGroupColumn'),
      notebookSettings: {
        isMinimapEnabled: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'web.user.notebookSettings.isMinimapEnabled'),
        formatOnSave: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'web.user.notebookSettings.formatOnSave'),
        python: {
          linter: {
            isEnabled: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'web.user.notebookSettings.python.linter.isEnabled'),
            config: LocalWorkspaceStorage.getJSONServerConfig(serverConfigValues, 'web.user.notebookSettings.python.linter.config')
          }
        }
      },
      webgl: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'web.webgl'),
      webglEditable: LocalWorkspaceStorage.getBooleanServerConfig(serverConfigValues, 'web.webgl.editable')
    };
    return merge({}, settings, serverSettings);
  }
  static makeWorkspaceData(layoutStorage, options) {
    return _asyncToGenerator(function* () {
      var _yield$UserLayoutUtil = yield UserLayoutUtils.getDefaultLayout(layoutStorage, options === null || options === void 0 ? void 0 : options.isConsoleAvailable),
        filterSets = _yield$UserLayoutUtil.filterSets,
        links = _yield$UserLayoutUtil.links,
        layoutConfig = _yield$UserLayoutUtil.layoutConfig,
        _yield$UserLayoutUtil2 = _yield$UserLayoutUtil.pluginDataMap,
        pluginDataMap = _yield$UserLayoutUtil2 === void 0 ? {} : _yield$UserLayoutUtil2;
      return {
        settings: {},
        layoutConfig,
        closed: [{}],
        links,
        filterSets,
        pluginDataMap
      };
    })();
  }
  static makeDefaultWorkspace(layoutStorage, options) {
    return _asyncToGenerator(function* () {
      return {
        data: yield LocalWorkspaceStorage.makeWorkspaceData(layoutStorage, options)
      };
    })();
  }
  constructor(layoutStorage) {
    _defineProperty(this, "layoutStorage", void 0);
    this.layoutStorage = layoutStorage;
  }

  // eslint-disable-next-line class-methods-use-this
  load(options) {
    var _this = this;
    return _asyncToGenerator(function* () {
      try {
        var _localStorage$getItem;
        var workspace = JSON.parse((_localStorage$getItem = localStorage.getItem(LocalWorkspaceStorage.STORAGE_KEY)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '');
        return workspace;
      } catch (e) {
        log.info('Unable to load workspace data, initializing to default data');
        return LocalWorkspaceStorage.makeDefaultWorkspace(_this.layoutStorage, options);
      }
    })();
  }

  // eslint-disable-next-line class-methods-use-this
  save(workspace) {
    return _asyncToGenerator(function* () {
      localStorage.setItem(LocalWorkspaceStorage.STORAGE_KEY, JSON.stringify(workspace));
      return workspace;
    })();
  }
}
_defineProperty(LocalWorkspaceStorage, "STORAGE_KEY", 'deephaven.WorkspaceStorage');
export default LocalWorkspaceStorage;
//# sourceMappingURL=LocalWorkspaceStorage.js.map